import classNames from 'classnames';
import { Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { AutoReloadSwitchRow } from './AutoReloadSwitchRow';
import { CreditTopupAutoReload } from './CreditTopupAutoReload';
import {
  CREDIT_TOPUP_PANEL_ANCHOR,
  CREDIT_TOPUP_RETURN_STATUS_QUERY_PARAM,
  getCreditTopupEntrypoint,
} from './creditTopupAnalytics';
import { recurringTaxNote } from './recurringTaxNote';
import { useCreditTopupCheckout } from './useCreditTopupCheckout';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { getUsageBarColor } from '@/Domain/components/TierPlanCard/usageColor';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useCreditTopupOffer } from '@/Domain/hooks/Subscription/useCreditTopupOffer.hook';

const BENEFIT_KEYS = ['never_expires', 'after_allowance'] as const;
const AUTO_RELOAD_CHOICE_ID = 'credit-topup-auto-reload-choice';
const PURCHASE_SUMMARY_ID = 'credit-topup-purchase-summary';

export const DiscordCreditTopupPanel = () => {
  const { t } = useTranslation('subscription_page');
  const { capture } = usePostHogAnalytics();
  const { offer, isEligible } = useCreditTopupOffer();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const entrypoint = getCreditTopupEntrypoint(searchParams);
  const [autoReloadChosen, setAutoReloadChosen] = useState(false);
  const checkoutReturnOnMount = useRef(searchParams.has(CREDIT_TOPUP_RETURN_STATUS_QUERY_PARAM));
  const panelViewTracked = useRef(false);
  const purchasePanelRef = useRef<HTMLElement>(null);
  const showsPurchasePanel = !!offer && isEligible;

  useEffect(() => {
    if (
      checkoutReturnOnMount.current ||
      panelViewTracked.current ||
      !offer ||
      (!isEligible && !offer.auto_reload.enabled)
    ) {
      return;
    }
    panelViewTracked.current = true;
    capture(PostHogEventEnum.CreditTopupPanelViewed, {
      entrypoint,
      credits: offer.credits,
      price_usd: offer.price_usd,
      topup_credits: offer.topup_credits,
      credits_purchased_total: offer.credits_purchased_total,
      auto_reload_available: offer.auto_reload.available,
      auto_reload_enabled: offer.auto_reload.enabled,
    });
  }, [capture, entrypoint, isEligible, offer]);

  // Keyed on location.key so a repeat click on the muted banner from Billing reveals the pack again.
  useEffect(() => {
    if (showsPurchasePanel && location.hash === `#${CREDIT_TOPUP_PANEL_ANCHOR}`) {
      purchasePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.key, location.hash, showsPurchasePanel]);

  const { mutate: startCheckout, isPending } = useCreditTopupCheckout(offer);

  if (!offer) return null;

  if (!isEligible) {
    if (!offer.auto_reload.enabled) return null;
    return (
      <section
        className="rounded-2xl border border-border bg-card p-6"
        data-testid="credit-topup-auto-reload-paused"
      >
        <div className="max-w-xl">
          <h2 className="text-base font-semibold text-foreground">
            {t('topup_panel.auto_reload.unavailable_title')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('topup_panel.auto_reload.unavailable_description')}
          </p>
          <div className="mt-4">
            <CreditTopupAutoReload
              offer={offer}
              offerCardSetup={false}
              canEnable={false}
              entrypoint={entrypoint}
            />
          </div>
        </div>
      </section>
    );
  }

  const credits = offer.credits.toLocaleString();
  const balance = offer.topup_credits;
  const hasBalance = balance > 0;
  // The immutable purchase ledger is the source for lifetime purchased credits.
  const purchasedTotal = Math.max(offer.credits_purchased_total, balance);
  const used = Math.max(0, purchasedTotal - balance);
  const percentUsed = purchasedTotal > 0 ? Math.min(100, (used / purchasedTotal) * 100) : 0;

  const showInitialPurchaseChoices = !hasBalance && !offer.auto_reload.available;
  const buyLabelKey = showInitialPurchaseChoices
    ? 'topup_panel.add'
    : hasBalance
      ? 'topup_panel.buy_more'
      : 'topup_panel.buy';

  const purchaseButton = (
    <Button
      variant="custom"
      className="h-auto min-h-10 w-full whitespace-normal border-0 bg-[var(--qc-discord-blurple)] py-2.5 text-center leading-snug text-white hover:bg-[var(--qc-discord-blurple-hover)]"
      disabled={isPending}
      loading={isPending}
      onClick={() =>
        startCheckout({ auto_reload: showInitialPurchaseChoices && autoReloadChosen, entrypoint })
      }
      aria-describedby={showInitialPurchaseChoices ? PURCHASE_SUMMARY_ID : undefined}
      data-testid={
        showInitialPurchaseChoices ? 'credit-topup-buy-button' : 'credit-topup-buy-once-button'
      }
    >
      {t(buyLabelKey, { credits, price: offer.price_usd })}
      <span className="text-xs font-normal opacity-75" data-testid="credit-topup-button-tax-note">
        {t('pricing.excl_vat')}
      </span>
    </Button>
  );

  return (
    <section
      ref={purchasePanelRef}
      data-brand="discord"
      data-testid="credit-topup-panel"
      className="rounded-2xl border border-border bg-card p-6 sm:p-8"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-[var(--qc-discord-blurple)] text-white"
              data-testid="credit-topup-discord-mark"
            >
              <DiscordIcon className="size-5" style={{ fill: 'currentColor' }} aria-hidden="true" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('topup_panel.badge')}
            </span>
          </div>

          {hasBalance ? (
            <div className="flex flex-col gap-2" data-testid="credit-topup-balance">
              <h2 className="text-2xl font-semibold tracking-tight text-foreground">
                {t('topup_panel.balance_headline', { credits: balance.toLocaleString() })}
              </h2>
              <div className="h-2 w-full max-w-sm overflow-hidden rounded-full bg-muted-foreground/15">
                <div
                  className={classNames(
                    'h-full rounded-full transition-all duration-500 ease-out',
                    getUsageBarColor(percentUsed),
                  )}
                  style={{ width: `${percentUsed}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground" data-testid="credit-topup-used">
                {t('topup_panel.used_of_total', {
                  used: used.toLocaleString(),
                  total: purchasedTotal.toLocaleString(),
                })}
              </p>
            </div>
          ) : (
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-foreground">
                {t('topup_panel.title', { credits, price: offer.price_usd })}
                <span
                  className="ml-1.5 text-xs font-normal tracking-normal text-foreground-subtle"
                  data-testid="credit-topup-headline-tax-note"
                >
                  {t('pricing.excl_vat')}
                </span>
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">{t('topup_panel.unit_price')}</p>
            </div>
          )}

          <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
            {BENEFIT_KEYS.map((key) => (
              <li key={key} className="flex items-start gap-2">
                <Check
                  className="mt-0.5 size-4 shrink-0 text-[var(--qc-discord-blurple)]"
                  aria-hidden="true"
                />
                <span>{t(`topup_panel.benefits.${key}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex w-full shrink-0 flex-col gap-4 rounded-xl bg-muted/50 p-5 lg:w-96">
          {showInitialPurchaseChoices ? (
            <div className="flex flex-col gap-3">
              {purchaseButton}
              <AutoReloadSwitchRow
                id={AUTO_RELOAD_CHOICE_ID}
                checked={autoReloadChosen}
                disabled={isPending}
                label={t(
                  autoReloadChosen
                    ? 'topup_panel.auto_reload.state_on'
                    : 'topup_panel.auto_reload.state_off',
                )}
                description={
                  autoReloadChosen
                    ? t('topup_panel.summary_auto_reload', {
                        credits,
                        price: offer.price_usd,
                        threshold: offer.auto_reload.threshold_credits.toLocaleString(),
                        tax: recurringTaxNote(
                          t('pricing.excl_vat'),
                          offer.auto_reload.tax_included,
                        ),
                      })
                    : t('topup_panel.summary_one_time', { price: offer.price_usd })
                }
                descriptionId={PURCHASE_SUMMARY_ID}
                descriptionTestId="credit-topup-purchase-summary"
                switchTestId="credit-topup-auto-reload-choice"
                onCheckedChange={setAutoReloadChosen}
              />
            </div>
          ) : (
            <>
              {purchaseButton}
              <CreditTopupAutoReload
                offer={offer}
                offerCardSetup={hasBalance}
                entrypoint={entrypoint}
              />
            </>
          )}

          {offer.auto_reload.tax_included && (
            <p className="text-xs text-foreground-subtle" data-testid="credit-topup-tax-note">
              {t('topup_panel.tax_note_auto_reload_unchanged')}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};
